import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { UserRole } from '../../types';
import api from '../../services/api';
import { Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import FormError from '../../components/shared/FormError';
import ServerError from '../../components/shared/ServerError';

const loginSchema = z.object({
  email: z.string().email('Valid email required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setServerError(null);
    try {
      const res = await api.post('/auth/login', data);
      const payload = res.data.data ?? res.data;

      if (payload.type === 'must_change_password') {
        navigate('/auth/change-password', { state: { email: data.email } });
        return;
      }

      const { accessToken, refreshToken, user } = payload;
      setAuth(user, accessToken, refreshToken, rememberMe);
      if (user.tenantId) {
        localStorage.setItem('vqs_tenant_id', user.tenantId);
      } else {
        localStorage.removeItem('vqs_tenant_id');
      }
      const dest =
        user.role === UserRole.SUPER_ADMIN ? '/tenants' :
        user.role === UserRole.GROUP_ADMIN ? '/group-admin' :
        '/dashboard';
      navigate(dest);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };
      const backendMessage = axiosErr?.response?.data?.errors?.[0]?.message;
      setServerError(backendMessage ?? 'Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white overflow-hidden font-sans select-none">
      {/* Background Brand Anchor (Large Vertical Text) - Desktop Only */}
      <div className="absolute top-0 right-0 h-full hidden xl:flex items-center pointer-events-none pr-12">
        <h1 className="text-[min(12vw,14vh)] font-bold text-slate-100 uppercase tracking-tighter transform rotate-[-90deg] origin-right translate-y-1/2 leading-none opacity-70 whitespace-nowrap">
          Sales
        </h1>
      </div>

      <div className="container relative mx-auto px-6 lg:px-12 py-12 min-h-screen flex flex-col justify-center max-w-[1440px]">
        {/* Glow Effect Element */}
        <div
          className="absolute pointer-events-none opacity-60"
          style={{
            top: '-14%',
            left: '-28%',
            width: '80%',
            height: '26%',
            background: 'radial-gradient(circle at top left, #0099d681, #0000 60%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="grid lg:grid-cols-2 gap-20 2xl:gap-32 items-center w-full relative z-10">
          {/* Main Content Area (Sales Branding) */}
          <div className="max-w-2xl space-y-10">
            {/* Branding Section */}
            <div className="space-y-4">
              <img
                src="/truvideo-logo-dark.png"
                alt="TruVideo"
                className="w-auto h-8 lg:h-10"
              />
              <div className="h-0.5 w-12 bg-primary rounded-full"></div>
            </div>

            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                Precision Auto <br className="hidden sm:block" />
                <span className="text-primary font-black">Intelligence.</span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-400 max-w-lg leading-relaxed font-medium italic">
                Driving technical authority through verified visual data.
              </p>
            </div>

            {/* Technical Metadata Labels (Original Sales Content) */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-slate-100 max-w-lg">
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Efficiency</div>
                <div className="text-3xl font-black text-primary">+42%</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Trust Index</div>
                <div className="text-3xl font-black text-primary">99.8</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Status</div>
                <div className="text-xl font-bold italic text-slate-400">Optimal</div>
              </div>
            </div>

            <div className="flex gap-4 items-center pt-8">
              <div className="h-px w-12 bg-slate-200"></div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-bold">
                Professional Workflows
              </span>
            </div>
          </div>

          {/* Form Section (1:1 UI Port) */}
          <div className="w-full flex justify-center lg:justify-start">
            <div className="w-full max-w-lg">
              <div className="w-full">
                <div className="mb-12 flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <ShieldCheck className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Sign In</h3>
                    <p className="text-slate-400 text-sm font-medium">Access the architectural core of your sales operations</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-8">
                  <ServerError message={serverError} className="mb-2" />

                  <div className="space-y-6">
                    {/* Business Email Input */}
                    <div className="space-y-3">
                      <label htmlFor="email" className="text-sm font-semibold text-slate-700 ml-1">
                        Business Email <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="email"
                          type="email"
                          placeholder="NAME@ENTERPRISE.COM"
                          autoComplete="email"
                          className={`w-full h-14 rounded-xl border px-6 text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-4 transition-all ${
                            errors.email
                              ? 'border-red-300 bg-red-50/50 focus:border-red-400 focus:ring-red-500/5'
                              : 'border-slate-100 bg-slate-50 focus:bg-white focus:border-primary focus:ring-primary/5'
                          }`}
                          aria-invalid={!!errors.email}
                          aria-describedby={errors.email ? 'email-error' : undefined}
                          {...register('email')}
                        />
                      </div>
                      <FormError message={errors.email?.message} />
                    </div>

                    {/* Security Access Key Input */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between ml-1">
                        <label htmlFor="password" className="text-sm font-semibold text-slate-700">
                          Security Access Key <span className="text-red-500 ml-1">*</span>
                        </label>
                        <Link to="/auth/forgot-password" className="text-xs font-bold text-primary hover:text-primary/70 transition-colors tracking-wider">
                          Forgot password?
                        </Link>
                      </div>
                      <div className="relative">
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className={`w-full h-14 rounded-xl border px-6 pr-12 text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-4 transition-all outline-none ${
                            errors.password
                              ? 'border-red-300 bg-red-50/50 focus:border-red-400 focus:ring-red-500/5'
                              : 'border-slate-100 bg-slate-50 focus:bg-white focus:border-primary focus:ring-primary/5'
                          }`}
                          aria-invalid={!!errors.password}
                          {...register('password')}
                        />
                        <button
                          type="button"
                          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-primary transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <FormError message={errors.password?.message} />
                    </div>

                    {/* Remember me */}
                    <div className="flex items-center gap-3 px-1">
                      <input
                        id="rememberMe"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 rounded-md border-slate-200 bg-slate-50 text-primary focus:ring-0 accent-primary cursor-pointer transition-all"
                      />
                      <label htmlFor="rememberMe" className="text-sm text-slate-500 font-semibold cursor-pointer select-none">
                        Remember Me
                      </label>
                    </div>
                  </div>

                  {/* Submit button (Original Sales Text) */}
                  <button
                    type="submit"
                    className="w-full h-16 rounded-xl text-sm font-bold shadow-sm hover:shadow-lg hover:translate-y-[-1px] active:translate-y-[0px] transition-all bg-slate-900 text-white hover:bg-primary cursor-pointer flex items-center justify-center gap-3 uppercase tracking-[0.1em]"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 lg:mt-0 lg:absolute lg:bottom-12">
          <p className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase">
            &copy; 2026 TruVideo &bull; All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
